import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { ImageGalleriesImageContent } from 'components/minute/sidebar/MarkerSidebar/shared/ImageGalleriesSubsection/ImageGalleriesImageContent';
import { OptionRowComponentSwitch } from 'components/minute/sidebar/shared/OptionRowComponentSwitch';
import { ColorPalette } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { DEFAULT_MAP_IMAGES } from 'hooks/map/usePreloadDefaultImages';
import {
    mapIconPreviewIndoorStandPinToInitialDate,
    useIconPreview
} from 'hooks/marker/useIconPreview';
import { EImageCategories } from 'libs/images/ImageGalleryData';
import { useContext } from 'react';
import { IIndoorStandWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IIndoorStandWorldMarker;
}

export const IndoorStandMarkerPinSection = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const directMarkerUpdates = useDirectMarkerUpdates();

    const iconPreview = useIconPreview({
        mapRef: ctxEdit.mapRef,
        worldMarkers: ctxEdit.worldMarkers,
        markerIds: [props.marker.id],
        initialData: mapIconPreviewIndoorStandPinToInitialDate(props.marker.elementData),
        markerType: 'icon',
        onNewImageGenerated: ({ isInitialLoad }) => {
            if (isInitialLoad) {
                return;
            }
            const newMarker: IIndoorStandWorldMarker = {
                ...props.marker,
                elementData: {
                    ...props.marker.elementData,
                    pinSrc: `temp-icon-${props.marker.id}`
                }
            };
            directMarkerUpdates.updateIconLikeMarker('indoor_stand', newMarker);
            ctxEdit.updateWorldMarkerData(props.marker.id, {
                pinSrc: `temp-icon-${props.marker.id}`
            });
        }
    });

    const options = [
        {
            menuItem: { text: Localization.COLORED_PIN },
            element: (
                <ColorPalette
                    value={props.marker.elementData.pinGeneratedIconColor}
                    pickerAllowed={true}
                    transparentAllowed={false}
                    onChange={(color) => {
                        console.log('SET_COLOR', 4);
                        iconPreview.setIconColor(color);
                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                            pinGeneratedIconColor: color
                        });
                    }}
                />
            )
        },
        {
            menuItem: { text: Localization.CUSTOM_IMAGE },
            element: (
                <ImageGalleriesImageContent
                    title={Localization.PIN_IMAGE}
                    dragAndDropEnabled
                    selectedImageUrl={props.marker.elementData.pinGeneratedImageUrl}
                    galleryType={'icon'}
                    onSelected={(imageUrl) => {
                        console.log('SET_IMAGE', 1);
                        iconPreview.setImage(imageUrl);
                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                            pinGeneratedImageUrl: imageUrl
                        });
                    }}
                    hideSearch={true}
                    hideCategoryFilter={true}
                    hideOpenModal={true}
                    initialRowsVisible={2}
                    additionalImages={[
                        DEFAULT_MAP_IMAGES.indoorStandGreen,
                        DEFAULT_MAP_IMAGES.indoorStandRed
                    ]}
                    initialCategory={EImageCategories.YOURS}
                />
            )
        }
    ];

    return (
        <SidebarSection title={Localization.STAND_MARKER2} icon={{ iconName: 'image' }} topBorder>
            <SidebarContent withTopPadding>
                <OptionRowComponentSwitch
                    initialSelectedIndex={
                        props.marker.elementData.pinGeneratedType === 'icon' ? 0 : 1
                    }
                    options={options}
                    onSelected={(index) => {
                        console.log('SET_TYPE', 3);
                        if (index === 0) {
                            iconPreview.setType({
                                type: 'icon',
                                src: props.marker.elementData.pinGeneratedIconUrl,
                                color: props.marker.elementData.pinGeneratedIconColor
                            });
                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                pinGeneratedType: 'icon'
                            });
                        } else {
                            iconPreview.setType({
                                type: 'image',
                                src: props.marker.elementData.pinGeneratedImageUrl
                            });
                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                pinGeneratedType: 'image'
                            });
                        }
                    }}
                />
            </SidebarContent>
            <S_Canvas ref={iconPreview.canvasRef} width={200} height={200} />
        </SidebarSection>
    );
};

const S_Canvas = styled.canvas`
    width: 100px;
    height: 100px;
    display: none;
`;
